import { useEffect, useRef } from 'react';
import {
  X,
  Mail,
  Phone,
  MapPin,
  Globe,
  ExternalLink,
  Loader2,
  AlertCircle,
  Tag,
  Building2,
} from 'lucide-react';

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export interface OrgDetail {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    county: string | null;
    postcode: string | null;
    country: string | null;
  } | null;
  type: string | null;
  tags: string[];
  createdAt: string | null;
  updatedAt: string | null;
  beaconAccountId: string | null;
}

interface OrgDetailPanelProps {
  orgId: number | null;
  orgName?: string; // For showing name while loading
  detail: OrgDetail | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
}

export default function OrgDetailPanel({
  orgId,
  orgName,
  detail,
  isLoading,
  error,
  onClose,
}: OrgDetailPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (orgId !== null) {
      document.body.style.overflow = 'hidden';
      panelRef.current?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [orgId]);

  if (orgId === null) return null;

  const addressLines = detail?.address
    ? [
        detail.address.line1,
        detail.address.line2,
        detail.address.city,
        detail.address.county,
        detail.address.postcode,
        detail.address.country,
      ].filter(Boolean)
    : [];

  const beaconUrl = detail?.beaconAccountId
    ? `https://app.beaconcrm.org/account/${detail.beaconAccountId}/record/organization/${orgId}`
    : null;

  const displayName = detail?.name || orgName || 'Loading…';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col outline-none"
        role="dialog"
        aria-modal="true"
        aria-label={`Organisation detail: ${displayName}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            {/* Icon */}
            <div className="h-14 w-14 flex-shrink-0 rounded-xl bg-sca-purple-50 flex items-center justify-center">
              {isLoading
                ? <Loader2 className="h-6 w-6 animate-spin text-sca-purple-400" />
                : <Building2 className="h-7 w-7 text-sca-purple-500" />
              }
            </div>

            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{displayName}</h2>
              {detail?.type && (
                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {detail.type}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            {beaconUrl && (
              <a
                href={beaconUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-sca-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="Open in Beacon CRM"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Loading state */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-sca-purple-400" />
              <p className="text-sm text-gray-500">Loading details…</p>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Failed to load details</p>
                <p className="text-sm text-red-600 mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {detail && !isLoading && (
            <>
              {/* Contact section */}
              <section>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Contact
                </h3>
                <div className="space-y-2">
                  {detail.email ? (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <a
                        href={`mailto:${detail.email}`}
                        className="text-sm text-sca-purple-600 hover:text-sca-purple-700 hover:underline truncate"
                      >
                        {detail.email}
                      </a>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-sm text-gray-400">
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      No email address
                    </div>
                  )}

                  {detail.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <a
                        href={`tel:${detail.phone}`}
                        className="text-sm text-sca-purple-600 hover:text-sca-purple-700 hover:underline"
                      >
                        {detail.phone}
                      </a>
                    </div>
                  )}

                  {detail.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <a
                        href={detail.website.startsWith('http') ? detail.website : `https://${detail.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-sca-purple-600 hover:text-sca-purple-700 hover:underline truncate"
                      >
                        {detail.website}
                      </a>
                    </div>
                  )}

                  {addressLines.length > 0 && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {addressLines.join(', ')}
                      </p>
                    </div>
                  )}

                  {!detail.email && !detail.phone && !detail.website && addressLines.length === 0 && (
                    <p className="text-sm text-gray-400">No contact details recorded</p>
                  )}
                </div>
              </section>

              {/* Tags */}
              {detail.tags.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-sca-purple-50 text-sca-purple-700 text-xs font-medium rounded-full border border-sca-purple-100"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Record metadata */}
              <section className="border-t border-gray-100 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Added</p>
                    <p className="text-xs font-medium text-gray-600">{formatDate(detail.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Last modified</p>
                    <p className="text-xs font-medium text-gray-600">{formatDate(detail.updatedAt)}</p>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        {/* Footer — open in Beacon link */}
        {beaconUrl && (
          <div className="flex-shrink-0 border-t border-gray-200 p-4">
            <a
              href={beaconUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-sca-purple-600 hover:text-sca-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
            >
              Open full record in Beacon CRM
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
      </div>
    </>
  );
}
